import { useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../../../integrations/firebase';

/**
 * Live view of channels/{id} — the server-written authority for a channel's members,
 * admins and posting policy. Only members can read it, so a member who is removed sees
 * `removed: true` the moment it happens.
 *
 * @returns {{channel: object|null, loading: boolean, removed: boolean, isAdmin: boolean, isOwner: boolean, canPost: boolean}}
 */
export function useChannel(chatId, enabled, uid) {
    const [state, setState] = useState({ channel: null, loading: false, removed: false });

    useEffect(() => {
        if (!enabled || !chatId) { setState({ channel: null, loading: false, removed: false }); return undefined; }
        setState({ channel: null, loading: true, removed: false });
        return onValue(ref(db, `channels/${chatId}`),
            (snap) => setState({ channel: snap.val(), loading: false, removed: !snap.exists() }),
            () => setState({ channel: null, loading: false, removed: true }));
    }, [chatId, enabled]);

    const ch = state.channel;
    const isMember = !!ch?.members && ch.members[uid] === true;
    const isOwner = isMember && ch.ownerId === uid;
    const isAdmin = isOwner || (isMember && !!ch?.admins && ch.admins[uid] === true);
    // Mirrors the database rule; the rule is what actually enforces it.
    const canPost = isMember && ch?.archived !== true && (ch?.postingPolicy !== 'admins' || isAdmin);
    return { ...state, isAdmin, isOwner, canPost };
}
